<template>
  <div class="video-remote">
    <v-icon v-if="session.notClientCamera" icon="msf:videocam_off" :size="60" color="#949494" />
    <v-icon v-else-if="session.notClientPresent" icon="msf:pause" :size="60" color="#949494" />
    <div class="video-remote__pane" v-if="!session.notClientCamera" :class="{'video-hidden': session.notClientPresent}">
      <video
        ref="video"
        class="media media--contain"
        autoplay
        muted
        playsinline
        @loadeddata="updateVideoState"
        @resize="updateVideoState"
        @emptied="updateVideoState"
      ></video>
      <Transition name="fade">
        <div v-if="showLoading" class="video-remote__loading" data-test-id="video-loading">
          <div class="a-loader__icon a-loader__icon--lg">
            <div class="a-loader__icon__content"></div>
          </div>
          <div class="video-remote__loading__message">
            {{ $t('meeting.stream.video.loading', {defaultValue: 'Video loading...'}) }}
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, watch, watchEffect, onUnmounted} from 'vue'
import {storeToRefs} from 'pinia'
import {useSessionStore, useTransformStore} from '@/stores'
import {useRemoteStream} from '@/composables'
import {useSnapshotOverlayStore} from '@/stores/snapshotOverlayStore'
import {useScreenShareStore} from '@/stores/screenshareStore'

const props = defineProps({hidden: Boolean})
const session = useSessionStore()
const transform = useTransformStore()
const {video: remoteVideoStream} = useRemoteStream()
const videoStore = useSnapshotOverlayStore()
const {videoElement: video} = storeToRefs(videoStore)
const screenShare = useScreenShareStore()
const {stream: screenShareStream, isScreenSharing, remoteScreenShare} = storeToRefs(screenShare)

const {zoom, x: tx, y: ty} = storeToRefs(transform)

const width = computed(() => (props.hidden ? '0' : `${session.transform.client.width * session.transform.scale}px`))
const height = computed(() => (props.hidden ? '0' : `${session.transform.client.height * session.transform.scale}px`))

const x = computed(() => tx.value * session.transform.scale)
const y = computed(() => ty.value * session.transform.scale)
const background = computed(() => (session.notClientCamera || session.notClientPresent ? '#383838' : '#000000'))
// "Video loading..." covers the black screen between the client's browser announcing its camera track and the first
// picture. A video that starts within LOADING_DELAY shows nothing. After LOADING_GIVE_UP the message goes away: by then
// the health check has renegotiated or warned about the camera, and "loading" would no longer be true
const LOADING_DELAY = 1000
const LOADING_GIVE_UP = 30000

const hasPicture = ref(false)
const hasLiveTrack = ref(false)
const showLoading = ref(false)
let loadingTimer = 0

function updateVideoState() {
  const element = video.value
  const track = remoteVideoStream.value?.getVideoTracks()[0]
  hasPicture.value = !!element && element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && element.videoWidth > 0
  // A client without a camera (none, or not allowed) announces no track; an ended one is left from an earlier connection
  hasLiveTrack.value = track?.readyState === 'live'
}

// No picture is coming while the client's camera is off or lost, or the client's page is hidden: the camera icon and
// the announcements say so. The client's profile arrives only once the call is connected; until then the announced
// track counts
const isVideoExpected = computed(() => {
  const profile = session.client?.profile
  return (
    !props.hidden &&
    !!session.client &&
    hasLiveTrack.value &&
    !hasPicture.value &&
    profile?.camera !== 'muted' &&
    profile?.camera !== 'not-available' &&
    profile?.presence !== false
  )
})

watchEffect(() => {
  if (video?.value) {
    if (isScreenSharing.value) {
      video.value.srcObject = screenShareStream.value
    } else if (remoteScreenShare.value) {
      video.value.srcObject = remoteScreenShare.value
    } else {
      video.value.srcObject = remoteVideoStream.value
      video.value.muted = false
    }
  }
  updateVideoState()
})

watch(
  isVideoExpected,
  expected => {
    clearInterval(loadingTimer)
    showLoading.value = false
    if (!expected) return
    const since = Date.now()
    // Polled too: a track that ends reports nothing to the element, and the first picture must not be missed
    loadingTimer = setInterval(() => {
      updateVideoState()
      const waited = Date.now() - since
      showLoading.value = isVideoExpected.value && waited >= LOADING_DELAY && waited < LOADING_GIVE_UP
    }, 250)
  },
  {immediate: true}
)

onUnmounted(() => clearInterval(loadingTimer))
</script>

<style lang="scss">
.video-remote {
  height: 100dvh;
  position: absolute;
  left: 0;
  top: 0;
  width: 100vw;
  overflow: hidden;
  background-color: v-bind(background);
  z-index: $zindex-video;
  touch-action: none;
  display: flex;
  justify-content: center;
  align-items: center;
  &__pane {
    width: v-bind(width);
    height: v-bind(height);
    video {
      .has--transition & {
        transition-property: transform;
        transition-duration: $duration-fast;
      }
      transform-origin: 0 0;
      transform: matrix(v-bind(zoom), 0, 0, (v-bind(zoom), v-bind(x), v-bind(y)));
    }
  }
  &__loading {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: $white;
    pointer-events: none;
    // the loader is drawn in the primary colour, for light backgrounds
    .a-loader__icon:before {
      background: conic-gradient($white, rgba($white, 0) 75%);
    }
    &__message {
      margin-top: $spacer;
      font-weight: $font-weight-bold;
    }
  }
}
.video-hidden {
  display: none;
}
</style>
